import { receiptClient } from './client';
import { ConnectError, Code } from '@connectrpc/connect';
import { create } from '@bufbuild/protobuf';
import { ProcessingStatus } from '../../generated/receipt/v1/receipt_pb';
import {
  SaveReceiptRequestSchema,
  UploadReceiptRequestSchema,
  CreateReceiptRequestSchema,
  ListReceiptsRequestSchema,
  GetReceiptRequestSchema,
  DeleteReceiptRequestSchema,
  SetItemDescriptionMappingRequestSchema,
  GetItemDescriptionMappingsRequestSchema,
  ListItemDescriptionMappingsRequestSchema,
  DeleteItemDescriptionMappingRequestSchema,
  SortField,
  SortOrder,
  type ReceiptItem,
  type SaleItem,
  type SaveReceiptResponse,
  type UploadReceiptResponse,
  type CreateReceiptResponse,
  type ListReceiptsResponse,
  type GetReceiptResponse,
  type DeleteReceiptResponse,
  type SetItemDescriptionMappingResponse,
  type GetItemDescriptionMappingsResponse,
  type ListItemDescriptionMappingsResponse,
  type DeleteItemDescriptionMappingResponse,
} from '../../generated/receipt/v1/receipt_pb';

export { SortField, SortOrder };

// Parameter interfaces - userId removed (auth interceptor adds JWT token)
export interface UploadReceiptParams {
  imageData: Uint8Array;
}

export interface SaveReceiptParams {
  receiptId: string;
  store: string;
  date: string;
  payment: string;
  tax: number;
  total: number;
  items: ReceiptItem[];
  saleItems: SaleItem[];
}

export interface GetReceiptParams {
  receiptId: string;
}

export interface DeleteReceiptParams {
  receiptId: string;
}

export interface CreateReceiptParams {
  store: string;
  date: string;
  payment: string;
  tax: number;
  total: number;
  items: ReceiptItem[];
  saleItems: SaleItem[];
}

export interface ListReceiptsParams {
  pageSize?: number;
  page?: number;
  sortBy?: SortField;
  sortOrder?: SortOrder;
  search?: string;
  storeFilter?: string;
  reconciliationStatusFilter?: string;
}

export interface SetItemDescriptionMappingParams {
  originalDescription: string;
  customDescription: string;
}

export interface DeleteItemDescriptionMappingParams {
  originalDescription: string;
}

export interface ListItemDescriptionMappingsParams {
  pageSize?: number;
  page?: number;
  search?: string;
}

/**
 * Upload receipt for OCR processing
 * User ID is extracted from JWT token by auth interceptor
 */
export const uploadReceipt = async (
  params: UploadReceiptParams
): Promise<UploadReceiptResponse> => {
  try {
    const request = create(UploadReceiptRequestSchema, {
      receiptPayload: params.imageData,
    });

    const response = await receiptClient.uploadReceipt(request);
    return response;
  } catch (error) {
    console.error('Receipt upload failed:', error);
    throw handleReceiptError(error, 'upload receipt');
  }
};

/**
 * Save edited receipt data
 * User ID is extracted from JWT token by auth interceptor
 */
export const saveReceipt = async (
  params: SaveReceiptParams
): Promise<SaveReceiptResponse> => {
  try {
    const request = create(SaveReceiptRequestSchema, {
      receiptId: params.receiptId,
      store: params.store,
      date: params.date,
      payment: params.payment,
      tax: params.tax,
      total: params.total,
      items: params.items,
      saleItems: params.saleItems,
    });

    const response = await receiptClient.saveReceipt(request);
    return response;
  } catch (error) {
    console.error('Receipt save failed:', error);
    throw handleReceiptError(error, 'save receipt');
  }
};

/**
 * Create a receipt manually without uploading an image
 * User ID is extracted from JWT token by auth interceptor
 */
export const createReceipt = async (
  params: CreateReceiptParams
): Promise<CreateReceiptResponse> => {
  try {
    const request = create(CreateReceiptRequestSchema, {
      store: params.store,
      date: params.date,
      payment: params.payment,
      tax: params.tax,
      total: params.total,
      items: params.items,
      saleItems: params.saleItems,
    });

    const response = await receiptClient.createReceipt(request);
    return response;
  } catch (error) {
    console.error('Create receipt failed:', error);
    throw handleReceiptError(error, 'create receipt');
  }
};

/**
 * List receipts for authenticated user with pagination and sorting
 * User ID is extracted from JWT token by auth interceptor
 */
export const listReceipts = async (params?: ListReceiptsParams): Promise<ListReceiptsResponse> => {
  try {
    const request = create(ListReceiptsRequestSchema, {
      pageSize: params?.pageSize,
      page: params?.page,
      sortBy: params?.sortBy,
      sortOrder: params?.sortOrder,
      search: params?.search,
      storeFilter: params?.storeFilter,
      reconciliationStatusFilter: params?.reconciliationStatusFilter,
    });

    const response = await receiptClient.listReceipts(request);
    return response;
  } catch (error) {
    console.error('List receipts failed:', error);
    throw handleReceiptError(error, 'list receipts');
  }
};

/**
 * Get single receipt by ID
 * User ID is extracted from JWT token by auth interceptor
 */
export const getReceipt = async (
  params: GetReceiptParams
): Promise<GetReceiptResponse> => {
  try {
    const request = create(GetReceiptRequestSchema, {
      receiptId: params.receiptId,
    });

    const response = await receiptClient.getReceipt(request);
    return response;
  } catch (error) {
    console.error('Get receipt failed:', error);
    throw handleReceiptError(error, 'get receipt');
  }
};

/**
 * Delete a receipt and all associated data
 * User ID is extracted from JWT token by auth interceptor
 */
export const deleteReceipt = async (
  params: DeleteReceiptParams
): Promise<DeleteReceiptResponse> => {
  try {
    const request = create(DeleteReceiptRequestSchema, {
      receiptId: params.receiptId,
    });

    const response = await receiptClient.deleteReceipt(request);
    return response;
  } catch (error) {
    console.error('Delete receipt failed:', error);
    throw handleReceiptError(error, 'delete receipt');
  }
};

/**
 * Set or update a custom description for an item
 * User ID is extracted from JWT token by auth interceptor
 */
export const setItemDescriptionMapping = async (
  params: SetItemDescriptionMappingParams
): Promise<SetItemDescriptionMappingResponse> => {
  try {
    const request = create(SetItemDescriptionMappingRequestSchema, {
      originalDescription: params.originalDescription,
      customDescription: params.customDescription,
    });

    const response = await receiptClient.setItemDescriptionMapping(request);
    return response;
  } catch (error) {
    console.error('Set item description mapping failed:', error);
    throw handleReceiptError(error, 'set item description');
  }
};

/**
 * Get all custom item descriptions for authenticated user
 * User ID is extracted from JWT token by auth interceptor
 */
export const getItemDescriptionMappings = async (): Promise<GetItemDescriptionMappingsResponse> => {
  try {
    const request = create(GetItemDescriptionMappingsRequestSchema, {});

    const response = await receiptClient.getItemDescriptionMappings(request);
    return response;
  } catch (error) {
    console.error('Get item description mappings failed:', error);
    throw handleReceiptError(error, 'get item descriptions');
  }
};

/**
 * List custom item descriptions with pagination and search
 * User ID is extracted from JWT token by auth interceptor
 */
export const listItemDescriptionMappings = async (
  params?: ListItemDescriptionMappingsParams
): Promise<ListItemDescriptionMappingsResponse> => {
  try {
    const request = create(ListItemDescriptionMappingsRequestSchema, {
      pageSize: params?.pageSize,
      page: params?.page,
      search: params?.search,
    });

    const response = await receiptClient.listItemDescriptionMappings(request);
    return response;
  } catch (error) {
    console.error('List item description mappings failed:', error);
    throw handleReceiptError(error, 'list item descriptions');
  }
};

/**
 * Delete a custom item description
 * User ID is extracted from JWT token by auth interceptor
 */
export const deleteItemDescriptionMapping = async (
  params: DeleteItemDescriptionMappingParams
): Promise<DeleteItemDescriptionMappingResponse> => {
  try {
    const request = create(DeleteItemDescriptionMappingRequestSchema, {
      originalDescription: params.originalDescription,
    });

    const response = await receiptClient.deleteItemDescriptionMapping(request);
    return response;
  } catch (error) {
    console.error('Delete item description mapping failed:', error);
    throw handleReceiptError(error, 'delete item description');
  }
};

const POLL_INTERVAL_MS = 3000;
const MAX_POLL_ATTEMPTS = 60; // 3 minutes max

/**
 * Poll GetReceipt until processing completes or fails.
 * Returns the completed GetReceiptResponse, or throws on failure/timeout.
 */
export const pollForProcessedReceipt = async (
  receiptId: string,
  onPoll?: (attempt: number) => void,
): Promise<GetReceiptResponse> => {
  for (let attempt = 0; attempt < MAX_POLL_ATTEMPTS; attempt++) {
    await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
    onPoll?.(attempt);

    const response = await getReceipt({ receiptId });

    if (response.processingStatus === ProcessingStatus.COMPLETED) {
      return response;
    }

    if (response.processingStatus === ProcessingStatus.FAILED) {
      throw new Error(response.errorMessage || 'Receipt processing failed');
    }
  }

  throw new Error('Receipt processing timed out. Check back later.');
};

/**
 * Handle receipt API errors with proper error code checking
 */
function handleReceiptError(error: unknown, operation: string): Error {
  if (error instanceof ConnectError) {
    // Check for auth errors (interceptor should handle these, but provide user-friendly message)
    if (error.code === Code.Unauthenticated) {
      return new Error('Session expired. Please log in again.');
    }
    if (error.code === Code.PermissionDenied) {
      return new Error('Access denied. You do not have permission to perform this action.');
    }

    // Check for invalid argument errors
    if (error.code === Code.InvalidArgument) {
      return new Error(`Invalid request: ${error.message || 'Please check your input'}`);
    }

    // Check for network errors in message (fallback for older ConnectRPC versions)
    const message = error.message || '';
    if (
      message.includes('Load failed') ||
      message.includes('Failed to fetch') ||
      message.includes('NetworkError')
    ) {
      return new Error(
        `Failed to ${operation}: Network error. Please check your connection and try again.`
      );
    }

    // Return error message if available
    if (error.message) {
      return new Error(`Failed to ${operation}: ${error.message}`);
    }
  }

  // Return generic error for unknown errors
  return new Error(
    `Failed to ${operation}: ${error instanceof Error ? error.message : 'Unknown error'}`
  );
}
